import { useState, useMemo, useCallback, useEffect } from 'react';
import { getBoundingBoxCategoryFromPaperItem } from '../../../utils/annotationUserdata';
import RoiNavigator from './RoiNavigator';
import { getRoiTypeLabel } from './BoundingBoxUI/roiProjectConfig';
import {
    getAllRoiFeatureItems,
    getReviewItemElementId,
    zoomToItem,
} from './roiNavigatorToolkit';

/**
 * Shared ROI jump UI for the viewer top bar (all annotation UIs that load ROIs into the toolkit).
 *
 * @param {object} props
 * @param {React.RefObject} props.toolkitRef
 * @param {boolean} props.toolkitReady
 * @param {object} props.roiConfig - from getRoiConfig(uiConfig)
 * @param {boolean} props.roiEditMode - when true, ROI editor uses bbox task bar; hide navigator
 * @param {string} props.itemId - current slide id (resets active index)
 * @param {boolean} props.disabled - e.g. completed or tools not ready
 * @param {number} props.roiLayerVersion - bump when hooks finish merging layers into the toolkit
 */
export default function ProjectRoiNavigator({
    toolkitRef,
    toolkitReady,
    roiConfig,
    roiEditMode = false,
    itemId,
    disabled = false,
    roiLayerVersion = 0,
}) {
    const [activeRoiIndex, setActiveRoiIndex] = useState(0);

    const roiItems = useMemo(() => {
        void itemId;
        void roiLayerVersion;
        const tk = toolkitRef?.current;
        if (!toolkitReady || !tk || !roiConfig?.enabled) return [];
        return getAllRoiFeatureItems(tk, roiConfig);
    }, [toolkitReady, toolkitRef, roiConfig, itemId, roiLayerVersion]);

    useEffect(() => {
        setActiveRoiIndex(0);
    }, [itemId, roiLayerVersion]);

    const handleRoiSelectIndex = useCallback(
        (idx) => {
            const tk = toolkitRef?.current;
            if (!tk || !roiConfig?.enabled) return;
            const items = getAllRoiFeatureItems(tk, roiConfig);
            if (!items.length) return;
            const j = Math.min(Math.max(idx, 0), items.length - 1);
            const item = items[j];
            if (!item) return;
            setActiveRoiIndex(j);
            zoomToItem(tk, item, { margin: 0.25 });
        },
        [toolkitRef, roiConfig],
    );

    const roiNavigatorRois = useMemo(
        () =>
            roiItems.map((item, i) => {
                const cid = getBoundingBoxCategoryFromPaperItem(item).categoryId;
                return {
                    id: getReviewItemElementId(item) || `roi-${i}`,
                    label: getRoiTypeLabel(cid, roiConfig) || `Region ${i + 1}`,
                };
            }),
        [roiItems, roiConfig],
    );

    const show =
        !roiEditMode && roiConfig?.enabled && roiNavigatorRois.length > 0;

    if (!show) return null;

    const safeRoiIndex = roiItems.length
        ? Math.min(Math.max(activeRoiIndex, 0), roiItems.length - 1)
        : 0;

    return (
        <RoiNavigator
            key={`${itemId}-${roiLayerVersion}`}
            rois={roiNavigatorRois}
            activeIndex={safeRoiIndex}
            onSelectIndex={handleRoiSelectIndex}
            disabled={disabled}
        />
    );
}
